// src/pages/analytics/SavedQueriesPage.tsx
// /analytics/queries — saved queries: browse, create, run, edit, delete.
// ListSavedQueries, CreateSavedQuery (via dialog), run/edit/delete in the rail.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { Button, Badge, Alert } from '@react/components';
import { analyticsApi } from '@core/api';
import type { AvailableDataSource, SavedQuery } from '@core/types/analytics';
import { useScope } from '@/hooks/useScope';
import { SavedQueryDialog, SavedQueryRail, errMessage, listParams } from '@/features/analytics';

export function SavedQueriesPage() {
  const { t } = useI18n();
  const scope = useScope();
  const [queries, setQueries] = useState<SavedQuery[]>([]);
  const [dataSources, setDataSources] = useState<AvailableDataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SavedQuery | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    analyticsApi
      .ListSavedQueries(listParams({ limit: 50 }))
      .then((res) => setQueries(res.items?.items ?? []))
      .catch((e: unknown) => setError(errMessage(e, t('analytics.queries.loadError'))))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    analyticsApi.ListAvailableDataSources().then((res) => setDataSources(res.data_sources ?? [])).catch(() => undefined);
  }, []);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  return (
    <div className="flex h-full">
      <div className="min-w-0 flex-1 space-y-4 overflow-y-auto p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-semibold text-foreground">{t('analytics.queries.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('analytics.queries.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/analytics" className="text-sm text-primary hover:underline">
              {t('analytics.backToDashboards')}
            </Link>
            <Button size="sm" onClick={openCreate}>
              {t('analytics.queries.create')}
            </Button>
          </div>
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        {loading && (
          <div className="space-y-2" aria-busy="true">
            <div className="h-16 animate-pulse rounded-md bg-muted" />
            <div className="h-16 animate-pulse rounded-md bg-muted" />
          </div>
        )}

        {!loading && queries.length === 0 && !error && (
          <div className="rounded-md border border-dashed border-border p-10 text-center">
            <p className="text-sm text-muted-foreground">{t('analytics.queries.empty')}</p>
            <Button size="sm" className="mt-3" onClick={openCreate}>
              {t('analytics.queries.create')}
            </Button>
          </div>
        )}

        <div className="space-y-2">
          {queries.map((q) => (
            <button
              key={q.entity?.id}
              onClick={() => setSelectedId(q.entity?.id ?? null)}
              className={`w-full rounded-md border p-3 text-left hover:bg-muted ${selectedId === q.entity?.id ? 'border-primary' : 'border-border'}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-foreground">{q.name}</span>
                {q.is_shared && <Badge variant="primary">{t('analytics.queries.shared')}</Badge>}
              </div>
              {q.description && <p className="mt-1 text-sm text-muted-foreground">{q.description}</p>}
              <p className="mt-1 text-xs text-muted-foreground">
                {q.query_definition?.product_slug} / {q.query_definition?.entity_type}
              </p>
            </button>
          ))}
        </div>
      </div>

      {selectedId && (
        <SavedQueryRail
          queryId={selectedId}
          scope={scope}
          onClose={() => setSelectedId(null)}
          onEdit={(q) => {
            setEditing(q);
            setDialogOpen(true);
          }}
          onDeleted={() => {
            setSelectedId(null);
            load();
          }}
        />
      )}

      <SavedQueryDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        scope={scope}
        dataSources={dataSources}
        query={editing}
        onSaved={load}
      />
    </div>
  );
}
